import { useEffect, useMemo } from "react";
import { shallowEqual } from "react-redux";
import { useLocation } from "react-router-dom";

import { useSW } from "@/hooks/app/useSW";
import { useAppSelector } from "@/hooks/redux";
import { openReplayService } from "@/Services/OpenReplay";

export const useOpenReplay = () => {
    const { pathname } = useLocation();

    const { trackedUserIds } = useSW();
    const { id, role, lastName, firstName, areaId, phone } = useAppSelector(
        (state) => ({
            id: state.user.id,
            role: state.user.role,
            lastName: state.user.lastName,
            firstName: state.user.firstName,
            areaId: state.user.employee?.context?.areaId,
            phone: state.user.phone,
        }),
        shallowEqual,
    );

    const metadata = useMemo(
        () => ({
            role: String(role ?? ""),
            name: `${firstName ?? ""} ${lastName ?? ""}`.trim(),
            region: String(areaId ?? ""),
            phone: String(phone ?? ""),
        }),
        [firstName, lastName, phone, role, areaId],
    );

    useEffect(() => {
        if (!id) return;

        const init = async () => {
            if (!openReplayService.isColdStarted()) {
                await openReplayService.coldStart(String(id), metadata);
            } else {
                openReplayService.setUser(String(id));
                Object.entries(metadata).forEach(([key, value]) => {
                    openReplayService.setMetadata(key, value);
                });
            }

            if (trackedUserIds.includes(id)) {
                await openReplayService.startPermanentRecording();
            }
        };

        void init();
    }, [id, metadata, trackedUserIds]);

    useEffect(() => {
        const handleError = (event: ErrorEvent) => {
            void openReplayService.startOnError("window_error", {
                message: String(event.message ?? ""),
                filename: String(event.filename ?? ""),
                lineno: String(event.lineno ?? ""),
                colno: String(event.colno ?? ""),
                route: pathname,
            });
        };

        const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
            const reason =
                event.reason instanceof Error
                    ? event.reason.message
                    : String(event.reason ?? "");

            void openReplayService.startOnError("window_unhandledrejection", {
                message: reason,
                route: pathname,
            });
        };

        window.addEventListener("error", handleError);
        window.addEventListener("unhandledrejection", handleUnhandledRejection);

        return () => {
            window.removeEventListener("error", handleError);
            window.removeEventListener(
                "unhandledrejection",
                handleUnhandledRejection,
            );
        };
    }, [pathname]);
};
